import type { PaymentMethod, PaymentTerms } from './api'

/**
 * A frase de pagamento do contrato (docs/areas/administracao.md, RN-ADM-33). É gerada, nunca
 * digitada, e fica igual em todo documento. Esta é a mesma regra que a API aplica ao salvar;
 * aqui serve à pré-visualização "No contrato sai: …" sem esperar a rede. O valor gravado é o da API.
 */

const ORDER: PaymentMethod[] = ['pix', 'debit', 'credit', 'cash']

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  pix: 'PIX',
  debit: 'Débito',
  credit: 'Crédito',
  cash: 'Dinheiro',
}

function inOrder(methods: readonly PaymentMethod[]) {
  return ORDER.filter((method) => methods.includes(method))
}

/** "A" · "A ou B" · "A, B ou C". */
function prose(items: string[]) {
  if (items.length <= 1) return items[0] ?? ''
  if (items.length === 2) return `${items[0]} ou ${items[1]}`
  return `${items.slice(0, -1).join(', ')} ou ${items[items.length - 1]}`
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** 10 → "10%"; 7.5 → "7,5%". */
export function formatPercent(percent: number) {
  return `${Number.isInteger(percent) ? percent : String(percent).replace('.', ',')}%`
}

function longLabel(method: PaymentMethod, terms: PaymentTerms) {
  if (method === 'pix') return 'PIX'
  if (method === 'debit') return 'débito'
  if (method === 'cash') return 'dinheiro'
  const installments = terms.creditInstallments ?? 1
  if (installments <= 1) return 'crédito à vista'
  return `crédito em até ${installments}×${terms.isInterestFree ? ' sem juros' : ''}`
}

function shortLabel(method: PaymentMethod, terms: PaymentTerms) {
  if (method === 'pix') return 'PIX'
  if (method === 'debit') return 'débito'
  if (method === 'cash') return 'dinheiro'
  const installments = terms.creditInstallments ?? 1
  return installments <= 1 ? 'crédito' : `crédito até ${installments}×`
}

const simpleLabel = (method: PaymentMethod) =>
  method === 'pix' ? 'PIX' : method === 'debit' ? 'débito' : 'dinheiro'

/** `paymentTermsText`: o que sai no contrato. Vazio enquanto não há meio escolhido. */
export function paymentTermsText(terms: PaymentTerms): string {
  const methods = inOrder(terms.methods)
  if (methods.length === 0) return ''
  let text = `${capitalize(prose(methods.map((method) => longLabel(method, terms))))}.`
  const discount = terms.upfrontDiscount
  if (discount && discount.methods.length > 0) {
    const discountMethods = inOrder(discount.methods).map(simpleLabel)
    text += ` Pagamento à vista via ${prose(discountMethods)} com ${formatPercent(discount.percent)} de desconto.`
  }
  return text
}

/** `paymentTermsSummary`: a coluna PAGAMENTO da lista. */
export function paymentTermsSummary(terms: PaymentTerms): string {
  const methods = inOrder(terms.methods)
  if (methods.length === 0) return ''
  let summary = methods.map((method) => shortLabel(method, terms)).join(' · ')
  const discount = terms.upfrontDiscount
  if (discount && discount.methods.length > 0) {
    summary += ` · ${formatPercent(discount.percent)} à vista no ${inOrder(discount.methods).map(simpleLabel).join(' ou ')}`
  }
  return summary
}

/** Condição inicial de um procedimento novo: nada escolhido. */
export const EMPTY_PAYMENT_TERMS: PaymentTerms = {
  methods: [],
  creditInstallments: null,
  isInterestFree: null,
  upfrontDiscount: null,
}

/**
 * Mantém a condição coerente quando os meios mudam: parcelas só existem com crédito, e o desconto
 * à vista só vale para meios escolhidos, sem crédito (RN-ADM-32).
 */
export function withMethods(terms: PaymentTerms, methods: PaymentMethod[]): PaymentTerms {
  const hasCredit = methods.includes('credit')
  const discountMethods = (terms.upfrontDiscount?.methods ?? []).filter(
    (method) => methods.includes(method) && method !== 'credit',
  )
  return {
    methods: inOrder(methods),
    creditInstallments: hasCredit ? (terms.creditInstallments ?? 1) : null,
    isInterestFree: hasCredit ? (terms.isInterestFree ?? true) : null,
    upfrontDiscount:
      terms.upfrontDiscount && discountMethods.length > 0
        ? { percent: terms.upfrontDiscount.percent, methods: discountMethods }
        : null,
  }
}
